<template>
  <div class="admin-system-settings">
    <v-card class="pa-6 rounded-lg" elevation="2">
      <div class="d-flex align-center mb-6">
        <v-icon color="#F4811F" size="32" class="mr-3">mdi-broom</v-icon>
        <h2 class="text-h5 font-weight-bold">ตั้งค่าการล้างข้อมูลอัตโนมัติ</h2>
      </div>

      <v-divider class="mb-6"></v-divider>

      <v-row>
        <v-col cols="12" md="6">
          <div class="setting-label">
            <v-icon size="20" color="#0EA5E9" class="mr-1">mdi-cart-outline</v-icon>
            อายุของตะกร้าสินค้า (Cart)
          </div>
          <div class="setting-sub">ถ้าตะกร้าไม่มีการอัพเดทเกินช่วงเวลานี้ ระบบจะล้างทิ้งอัตโนมัติ</div>
          <v-row no-gutters dense>
            <v-col cols="7">
              <v-text-field
                v-model.number="cart.value"
                type="number"
                min="0"
                outlined
                dense
                hide-details
                class="mr-2"
              />
            </v-col>
            <v-col cols="5">
              <v-select
                v-model="cart.unit"
                :items="unitOptions"
                outlined
                dense
                hide-details
              />
            </v-col>
          </v-row>
          <div class="setting-hint">
            <template v-if="cart.value > 0">เทียบเท่า <b>{{ formatMinutes(cartMinutes) }}</b> ({{ cartMinutes }} นาที)</template>
            <template v-else>ใส่ 0 เพื่อปิดการทำงาน</template>
          </div>
        </v-col>

        <v-col cols="12" md="6">
          <div class="setting-label">
            <v-icon size="20" color="#EF4444" class="mr-1">mdi-receipt-text-remove-outline</v-icon>
            ยกเลิกคำสั่งซื้อที่ยังไม่ชำระเงินอัตโนมัติ
          </div>
          <div class="setting-sub">ถ้าคำสั่งซื้อสถานะ "รอชำระเงิน" ยังไม่จ่ายภายในช่วงเวลานี้ ระบบจะยกเลิกอัตโนมัติ</div>
          <v-row no-gutters dense>
            <v-col cols="7">
              <v-text-field
                v-model.number="order.value"
                type="number"
                min="0"
                outlined
                dense
                hide-details
                class="mr-2"
              />
            </v-col>
            <v-col cols="5">
              <v-select
                v-model="order.unit"
                :items="unitOptions"
                outlined
                dense
                hide-details
              />
            </v-col>
          </v-row>
          <div class="setting-hint">
            <template v-if="order.value > 0">เทียบเท่า <b>{{ formatMinutes(orderMinutes) }}</b> ({{ orderMinutes }} นาที)</template>
            <template v-else>ใส่ 0 เพื่อปิดการทำงาน</template>
          </div>
        </v-col>
      </v-row>

      <div class="d-flex justify-end mt-4">
        <v-btn
          color="#F4811F"
          dark
          depressed
          class="px-6"
          :loading="saving"
          @click="save"
        >
          <v-icon left>mdi-content-save</v-icon>
          บันทึก
        </v-btn>
      </div>
    </v-card>
  </div>
</template>

<script>
import AdminSettingsService from "@/services/api/adminSettings";
import swalAlert from "@/services/alert/swalServices";

const UNIT_MIN = { minute: 1, hour: 60, day: 60 * 24, month: 60 * 24 * 30 };

function bestUnit(minutes) {
  if (!minutes || minutes <= 0) return { value: 0, unit: "minute" };
  for (const u of ["month", "day", "hour"]) {
    if (minutes % UNIT_MIN[u] === 0) return { value: minutes / UNIT_MIN[u], unit: u };
  }
  return { value: minutes, unit: "minute" };
}

export default {
  name: "AdminSystemSettings",
  data() {
    return {
      loading: false,
      saving: false,
      cart:  { value: 0, unit: "minute" },
      order: { value: 0, unit: "minute" },
      unitOptions: [
        { text: "นาที",     value: "minute" },
        { text: "ชั่วโมง",  value: "hour" },
        { text: "วัน",      value: "day" },
        { text: "เดือน",    value: "month" },
      ],
    };
  },
  computed: {
    cartMinutes() {
      return Math.max(0, Math.round((Number(this.cart.value) || 0) * UNIT_MIN[this.cart.unit]));
    },
    orderMinutes() {
      return Math.max(0, Math.round((Number(this.order.value) || 0) * UNIT_MIN[this.order.unit]));
    },
  },
  async created() {
    await this.load();
  },
  methods: {
    formatMinutes(m) {
      if (!m) return "-";
      const parts = [];
      const months = Math.floor(m / UNIT_MIN.month); m -= months * UNIT_MIN.month;
      const days   = Math.floor(m / UNIT_MIN.day);   m -= days   * UNIT_MIN.day;
      const hours  = Math.floor(m / UNIT_MIN.hour);  m -= hours  * UNIT_MIN.hour;
      const mins   = m;
      if (months) parts.push(`${months} เดือน`);
      if (days)   parts.push(`${days} วัน`);
      if (hours)  parts.push(`${hours} ชั่วโมง`);
      if (mins)   parts.push(`${mins} นาที`);
      return parts.join(" ") || "0 นาที";
    },
    async load() {
      this.loading = true;
      try {
        const res = await AdminSettingsService.getSystemSettings();
        const d = res?.data?.data || {};
        const c = bestUnit(Number(d.cartExpiryMinutes) || 0);
        const o = bestUnit(Number(d.orderAutoCancelMinutes) || 0);
        this.cart  = c;
        this.order = o;
      } catch (e) {
        swalAlert.Error("ไม่สามารถโหลดข้อมูลได้", e?.response?.data?.message);
      } finally {
        this.loading = false;
      }
    },
    async save() {
      this.saving = true;
      try {
        const res = await AdminSettingsService.updateSystemSettings({
          cartExpiryMinutes: this.cartMinutes,
          orderAutoCancelMinutes: this.orderMinutes,
        });
        const c = res?.data?.cleanup;
        let msg = "บันทึกการตั้งค่าสำเร็จ";
        if (c && (c.cartsCleared > 0 || c.ordersCancelled > 0)) {
          msg += `\nล้างข้อมูลทันที: ลบตะกร้า ${c.cartsCleared} รายการ, ยกเลิกคำสั่งซื้อ ${c.ordersCancelled} รายการ`;
        }
        await swalAlert.Success(msg);
        await this.load();
      } catch (e) {
        swalAlert.Error("บันทึกไม่สำเร็จ", e?.response?.data?.message);
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.setting-label {
  font-size: 15px;
  font-weight: 700;
  color: #1f2a39;
  display: flex;
  align-items: center;
  margin-bottom: 2px;
}
.setting-sub {
  font-size: 12px;
  color: #6b717f;
  margin-bottom: 10px;
}
.setting-hint {
  font-size: 12px;
  color: #6b717f;
  margin-top: 6px;
  min-height: 18px;
}
</style>
